import { useEffect, useMemo, useState } from 'react';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../../utils/tauri';

// The last list Rust returned, kept for the session. A menu opens with it at
// once and re-checks the system in the background (owner, 26.09.2026: the
// SHELLS section popped in a moment after the "Split with…" picker opened and
// the list jumped). The re-check only replaces the list when it changed, so a
// newly installed or removed shell still shows up without a restart.
let cached = [];
let inflight = null;

const sameShells = (a, b) =>
  a.length === b.length && a.every((s, i) => s.key === b[i].key && s.label === b[i].label && s.path === b[i].path);

function refresh() {
  if (!inflight) {
    inflight = invoke('terminal_list_shells')
      .then((list) => {
        const next = Array.isArray(list) ? list : [];
        if (!sameShells(cached, next)) cached = next;
        return cached;
      })
      .catch((err) => {
        console.warn('[terminal] listing the local shells failed:', err);
        return cached;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** The installed local shells (`terminal_list_shells`): the cached list at once, re-checked on mount. */
export function useTerminalShells() {
  const [shells, setShells] = useState(cached);
  useEffect(() => {
    if (!isTauri()) return undefined;
    let alive = true;
    refresh().then((list) => alive && setShells(list));
    return () => {
      alive = false;
    };
  }, []);
  return shells;
}

/**
 * The shells a picker may offer: installed ones minus those hidden in
 * Settings → Extensions → Terminal → Shells. Every shell picker uses this, so
 * the filter lives in ONE place. Never empty while something is installed —
 * if every shell were hidden (an old file), they all show.
 */
export function useVisibleTerminalShells() {
  const shells = useTerminalShells();
  const hidden = useTerminalConfigStore((s) => s.prefs?.hiddenShells);
  return useMemo(() => {
    if (!Array.isArray(hidden) || hidden.length === 0) return shells;
    const visible = shells.filter((s) => !hidden.includes(s.key));
    return visible.length ? visible : shells;
  }, [shells, hidden]);
}

/** Test hook: forget the session cache. */
export function resetTerminalShellsCache() {
  cached = [];
  inflight = null;
}
